import React, { useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import CircularLoader from 'src/components/CircularLoader';
import { formatDateISO } from 'src/utils/date';
import useFetchPurchaseOrderNotes from 'src/hooks/useFetchPurchaseOrderNotes';
import { tableHeaders } from 'src/components/PurchaseOrderDetails/constants/tableHeaderConfig';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    minHeight: '100px',
    maxHeight: '300px',
    width: '100%',
  },
  cardContainer: {
    margin: theme.spacing(3),
  },
  tableControls: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: theme.spacing(2),
  },
  tableControlsRight: {
    marginRight: theme.spacing(2),
  },
  noResultsContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  loadingContainer: {
    position: 'absolute',
    top: '50%',
    left: '55%',
  },
  headerTableRow: {
    backgroundColor: theme.palette.background.default,
    width: '100%',
  },
  headerTableCell: {
    minWidth: theme.spacing(15),
    padding: theme.spacing(1),
  },
  tableCell: {
    padding: theme.spacing(2),
    width: '100px',
  },
  searchInput: {
    width: 500,
  },
}));

function PurchaseOrderNoteTable({ poNumber, dispatchId, refreshTable }) {
  const tableRef = useRef(null);
  const classes = useStyles();
  const {
    notes,
    isLoading,
    params,
    handleOrderBy,
    refresh,
  } = useFetchPurchaseOrderNotes(poNumber, dispatchId);

  useEffect(() => {
    if (refreshTable) {
      refresh();
      if (tableRef?.current) {
        tableRef.current.scrollTop = 0;
      }
    }
  }, [refreshTable]
  );

  function renderTableContents() {
    if (isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularLoader />
        </Box>
      );
    }

    return notes.map(row => <TableRow
      key={`${row.noteId}-${row.source}`}
      className={classes.tableRow}
    >
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '15%' }}
      >
        {formatDateISO(row.dateCreated)}
      </TableCell>
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '15%' }}
      >{row.createdByEmail}
      </TableCell>
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '10%' }}
      >{row.source}
      </TableCell>
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '10%' }}
      >{row.sourceId}
      </TableCell>
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '10%' }}
      >{row.noteTypeName}
      </TableCell>
      <TableCell
        align='left'
        padding='none'
        className={classes.tableCell}
        style={{ width: '40%' }}
      >{row.text}
      </TableCell>
    </TableRow>
    );
  }

  if (!notes?.length) {
    return (
      <Box className={classes.noResultsContainer}>
        <Typography variant='h5'>There are no notes for this Purchase Order</Typography>
      </Box>
    );
  }

  return (
    <Card className={classes.cardContainer}>
      <TableContainer className={classes.tableContainer} ref={tableRef}>
        <Table stickyHeader>
          <TableHead>
            <TableRow
              className={classes.headerTableRow}
            >
              {tableHeaders.map((headCell) => (
                <TableCell
                  key={headCell.value}
                  padding='none'
                  className={classes.headerTableCell}
                  align='left'
                  style={{ width: headCell.width }}
                >
                  {headCell.canOrderBy ? (<TableSortLabel
                    onClick={() => handleOrderBy(headCell.value)}
                    active={params?.orderBy === headCell.value}
                    direction={params?.order}
                    className={classes.orderColumn}
                  >
                    {headCell.label}
                  </TableSortLabel>) : `${headCell.label}`}

                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody style={{ height: '300px', width: '100%' }}>
            {renderTableContents()}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

PurchaseOrderNoteTable.propTypes = {
  poNumber: PropTypes.string,
  dispatchId: PropTypes.string,
  refreshTable: PropTypes.bool,
};

export default PurchaseOrderNoteTable;
